import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Label,
  CartesianGrid,
  ReferenceArea,
  ReferenceLine,
  LabelList,
} from "recharts";
import { FONT_SIZE_EXTRA_SMALL } from "../../../../../constants/constants";

import { ChartStyles } from "../../../../../pages/customers/pci/Styles/PciChartStyles";
import { GenericRechartsProps } from "../../../../../types";
import RotatedYAxisLabel from "../../../RechartHelpers/RotatedYAxisLabel";

/**
 * Renders a custom label for our downtime Pareto chart,
 * @param param0 Contains x, y, width, height, and contents for the text box we are to produce
 */
const CustomBarLabel = ({ x, y, width, height, value }) => {
  return (
    <text
      key={Math.random() * 1000}
      x={x + width}
      y={y + height}
      fill="#666"
      textAnchor="left"
    >
      {value}
    </text>
  );
};

/**
 * Renders a custom label for our downtime Pareto chart,
 * @param param0 Contains x, y, width, height, and contents for the text box we are to produce
 */
const CustomBarLabelLeft = ({ x, y, width, height, value }) => {
  return (
    <text
      key={Math.random() * 1000}
      x={x - 30}
      y={y + FONT_SIZE_EXTRA_SMALL + 3}
      fill="#666"
      textAnchor="right"
    >
      {value}
    </text>
  );
};

const customDateTick = ({ x, y, textAnchor, payload }) => {
  return (
    <text
      key={Math.random() * 1000}
      x={x}
      y={y + 10}
      textAnchor={textAnchor}
      stroke="#666"
      fontSize="12"
    >
      {payload.value}
    </text>
  );
};

/**
 * generate and create bar chart based on given data
 */
export default function WeeklyGenericBarChart({
  title,
  data,
  xLabel,
  xKey = "date",
  yLabel,
  yKey,
  bars,
  vertical = false,
  width = 1000,
  height = 500,
  useGrid = false,
  referenceAreas = [{} as any],
  referencelines = [{} as any],
  legend,
  customBars,
  tooltipLabelFormatter = null,
}: GenericRechartsProps) {
  return (
    <ChartStyles>
      <p className="chartTitle" key={title + ": actualTitle"}>
        {title}
      </p>
      <div className="chartBody" key={title + ": wrapper"}>
        <BarChart
          key={title}
          width={width * 0.95}
          height={height}
          data={data || undefined}
          margin={{
            top: 30,
            right: 60,
            left: vertical ? 60 : 5,
            bottom: 30,
          }}
          stackOffset="sign"
          layout={vertical ? "vertical" : "horizontal"}
        >
          {useGrid ? (
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              horizontal={true}
            />
          ) : null}
          {vertical ? null : legend}
          {vertical ? (
            <YAxis
              type="category"
              dataKey={yKey}
              tick={true}
              label={<RotatedYAxisLabel height={height} value={yLabel} />}
            />
          ) : (
            <XAxis key={xKey} dataKey={xKey} tick={customDateTick} interval={0}>
              <Label
                key={xLabel}
                value={xLabel}
                offset={-15}
                position="insideBottom"
              />
            </XAxis>
          )}
          {vertical ? (
            <XAxis type="number">
              <Label value={xLabel} offset={-15} position="insideBottom" />
            </XAxis>
          ) : (
            <YAxis
              type="number"
              orientation="left"
              label={<RotatedYAxisLabel height={height} value={yLabel} />}
            />
          )}

          <Tooltip labelFormatter={tooltipLabelFormatter} />
          {!customBars
            ? bars?.map(
                ({
                  key,
                  name,
                  stackId,
                  fill,
                  label,
                  labelContents,
                  label2,
                  label2Contents,
                }) => {
                  return (
                    <Bar
                      key={key}
                      dataKey={key}
                      name={name}
                      stackId={stackId}
                      fill={fill}
                      isAnimationActive={false}
                    >
                      {label ? (
                        !vertical ? (
                          <LabelList position="top" />
                        ) : (
                          <LabelList
                            dataKey={labelContents}
                            content={CustomBarLabel as any}
                            position="right"
                          />
                        )
                      ) : null}
                      {label2 ? (
                        !vertical ? (
                          <LabelList position="top" />
                        ) : (
                          <LabelList
                            dataKey={label2Contents}
                            content={CustomBarLabelLeft as any}
                            position="left"
                          />
                        )
                      ) : null}
                    </Bar>
                  );
                }
              )
            : customBars}

          {referencelines.map(
            ({ y, label, stroke, strokeDasharray, isFront, alwaysShow }) => {
              const key = label + y + stroke;
              return (
                <ReferenceLine
                  key={isNaN(key) ? Math.random() * 1000 : key}
                  label={label}
                  y={y}
                  ifOverflow={alwaysShow ? "extendDomain" : "hidden"}
                  isFront={isFront}
                  stroke={stroke}
                  strokeDasharray={strokeDasharray}
                />
              );
            }
          )}
          {referenceAreas.map(
            ({ label, y, x, x2, stroke, strokeDasharray }) => {
              const key = label + y + x + x2 + stroke;
              return (
                <ReferenceArea
                  key={isNaN(key) ? Math.random() * 1000 : key}
                  label={label}
                  y1={y}
                  y2={y * 1.000001}
                  x1={x}
                  x2={x2}
                  stroke={stroke}
                  strokeDasharray={strokeDasharray}
                />
              );
            }
          )}
        </BarChart>
      </div>
    </ChartStyles>
  );
}
